import { useCallback, useState, ChangeEvent, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useMutation, QueryClient, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Stack, StackProps } from 'components/Stack';
import { Text } from 'components/Typography';
import { Button } from 'components/Button';
import { ReactComponent as IconBoxFlat } from 'constants/icon/icon_box_flat.svg';
import { ReactComponent as IconPlugBlue } from 'constants/icon/ic_plus_blue.svg';
import { set, useForm } from 'react-hook-form';
import { AlertModal } from 'components/Modal/AlertModal';
import MessageLayout from 'pages/Message/common/MessageLayout';
import theme from 'constants/theme';
import { ISingleHistoryMessage, useSingleHistoryMessage, useHistoryRequestParams } from '../../HistoryStore';
import FixedMessageComponent from './FixedMessageComponent';
import MainMessageButtonDiv from './MainMessageButtonDiv';
import { IHistoryTableDataAuto } from '../table/HistoryTable';
import useToastStore from 'components/Toast/ToastStore';
import { useSelectingRow } from 'pages/Message/common/CheckSelectStore';
import RequestErrorModal from 'pages/Message/common/RequestErrorModal';
import ChangeMessageComponent from './ChangeMessageComponent';
import { deleteMessageManual, patchMessageManual } from 'pages/Message/api';
import { useIsDifferenceStore } from 'pages/Message/common/MessagePromptStore';
import { useTransferRecipent } from '../../HistoryStore';

export interface IChangeMessageLocalState extends IHistoryTableDataAuto {
  date?: string;
  hour?: string;
  minute?: string;
}

const MainMessageComponent = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { singleHistoryMessage, setSingleHistoryMessage } = useSingleHistoryMessage();
  const { requestParams } = useHistoryRequestParams();
  const { addToast } = useToastStore();
  const { selectRow, setselectRow } = useSelectingRow();
  const { setIsDifference } = useIsDifferenceStore();
  const { setTransferRecipent } = useTransferRecipent();

  const [isOnChange, setIsOnChange] = useState(false); // 메시지 수정 여부처리

  // 예약 취소 확인 모달
  const [isOpenCancelReservationModal, setIsOpenCancelReservationModal] = useState(false);
  // 생성하기 이탈 경고 모달
  const [isOpenCancelChangeModal, setIsOpenCancelChangeModal] = useState(false);

  // 예약 발송 취소 실패 모달
  const [isOpenCancelReservationFailModal, setIsOpenCancelReservationFailModal] = useState(false);
  // 수정 완료 실패 모달
  const [isOpenModifyFailModal, setIsOpenModifyFailModal] = useState(false);

  // 메시지 수정처리 관련 localState
  const { setValue, watch, reset } = useForm<Partial<IChangeMessageLocalState>>({
    defaultValues: {
      messageId: '',
      sendDate: '',
      recipentType: '',
      title: '',
      recipant_name: '',
      contents: '',
      recipent_infos: [],
      send_status: '',
    },
  });

  useEffect(() => {
    if (!!singleHistoryMessage) {
      // 다른 행 선택이 될 때 isonChange false 처리
      if (singleHistoryMessage.messageId !== watch('messageId')) {
        setIsOnChange(false);
      }
      setValue('messageId', singleHistoryMessage.messageId);
      setValue('title', singleHistoryMessage.title);
      setValue('contents', singleHistoryMessage.contents);
      setValue('date', dayjs(singleHistoryMessage.sendDate).format('YYYY.MM.DD'));
      setValue('hour', dayjs(singleHistoryMessage.sendDate).format('HH'));
      setValue('minute', dayjs(singleHistoryMessage.sendDate).format('mm'));
      setValue('recipent_infos', singleHistoryMessage.recipent_infos);
      setValue('sendDate', singleHistoryMessage.sendDate);
      if (singleHistoryMessage.sendMode) {
        setValue('sendMode', singleHistoryMessage.sendMode);
      }
    } else {
      // 발송이력조회에서 다른 탭으로 이동 시 아래와 같이 처리
      // 메시지 초기화 + isOnChange 초기화
      reset();
      setIsOnChange(false);
    }
  }, [singleHistoryMessage]);

  const { mutate: cancelReservationMutate } = useMutation({
    mutationFn: async (params: IHistoryTableDataAuto) => {
      return deleteMessageManual({ id: params.messageId });
    },
    onSuccess: async () => {
      // 성공 토스트 띄우기 + 데이터 초기화 처리 + 테이블 리스트 재요청 처리 + 모달 닫기
      addToast('발송 예약 메시지가 취소되었습니다.', 3000);
      setselectRow('');
      setSingleHistoryMessage(null);
      queryClient.invalidateQueries(['historyDataList', requestParams]);
      setIsOpenCancelReservationModal(false);
    },
    onError: (error, variables, context) => {
      // 모달 닫기 + 에러 토스트 띄우기
      setIsOpenCancelReservationModal(false);
    },
  });

  const { mutate: modifyRequestMutate } = useMutation({
    mutationFn: async (params: IChangeMessageLocalState) => {
      return patchMessageManual({
        id: params.messageId,
        receivers: params.recipent_infos.map(item => ({
          consigneeName: item.customer_name,
          consigneePhone: item.customer_phone,
        })),
        messageTitle: params.title,
        messageContents: params.contents,
        //@ts-expect-error
        sendMode: params.sendMode,
        reserveDatetime: params.sendDate,
      });
    },
    onSuccess: async e => {
      if (!!e?.statusCode) {
        if (e?.statusCode.startsWith('4') || e?.statusCode.startsWith('5')) {
          // 에러 모달 띄우기
          setIsOpenModifyFailModal(true);
          setIsOnChange(true);
          setIsDifference(true);
        } else {
          // 성공 토스트 띄우기 + 데이터 초기화 처리 + 테이블 리스트 재요청 처리 + 모달 닫기
          addToast('수정이 완료되었습니다.', 3000);
          setselectRow('');
          setSingleHistoryMessage(null);
          queryClient.invalidateQueries(['historyDataList', requestParams]);
          setIsOnChange(false);
          setIsDifference(false);
        }
      } else {
        // 성공 토스트 띄우기 + 데이터 초기화 처리 + 테이블 리스트 재요청 처리 + 모달 닫기
        addToast('수정이 완료되었습니다.', 3000);
        setselectRow('');
        setSingleHistoryMessage(null);
        queryClient.invalidateQueries(['historyDataList', requestParams]);
        setIsOnChange(false);
        setIsDifference(false);
      }
    },
    onError: (error, variables, context) => {
      // 에러 모달 띄우기
      setIsOpenModifyFailModal(true);
      setIsOnChange(true);
      setIsDifference(true);
    },
  });

  // 예약 발송 취소(버튼)
  const onClickCancelReservation = useCallback(() => {
    setIsOpenCancelReservationModal(true);
  }, [setIsOpenCancelReservationModal]);
  // 수정하기(버튼)
  const onClickModify = useCallback(() => {
    setIsOnChange(true);
    setIsDifference(true);
  }, [setIsOnChange]);
  // 취소(버튼)
  const onClickCancel = useCallback(() => {
    setIsOpenCancelChangeModal(true);
  }, [setIsOpenCancelChangeModal]);
  // 수정 완료(버튼)
  const onClickModifyComplete = useCallback(() => {
    modifyRequestMutate({
      ...watch(),
      sendDate: `${watch('date') ?? ''} ${watch('hour') ?? ''}:${watch('minute') ?? ''}`,
      messageId: watch('messageId') ?? '',
      recipentType: watch('recipentType') ?? '',
      title: watch('title') ?? '',
      recipant_name: watch('recipant_name') ?? '',
      contents: watch('contents') ?? '',
      recipent_infos: watch('recipent_infos') ?? [],
      send_status: watch('send_status') ?? '',
    });
  }, [watch()]);
  // 이 수취인에게 새 매시지 작성하기(버튼)
  const onClickAddRecipientButton = () => {
    const customerInfos = watch('recipent_infos');
    if (!!customerInfos && customerInfos.length > 0) {
      setTransferRecipent(
        customerInfos.map(item => ({
          customer_name: item.customer_name,
          customer_phone: item.customer_phone,
        }))
      );
      return navigate('/manage/message/manual');
    }
  };

  // 모달 버튼
  // 예약 발송 취소(모달 버튼)
  const onClickRequestCancelReservation = () => {
    if (singleHistoryMessage) {
      cancelReservationMutate(singleHistoryMessage);
    }
  };
  // 닫기 (수정하기 취소 모달 버튼)
  const onClickModalWriteCancel = () => {
    // 모달 닫기 + 선택한 row 그대로 + 메시지 초기화 + 수정여부 초기화
    setIsOpenCancelChangeModal(false);
    // setselectRow('');
    // setSingleHistoryMessage(null);
    setIsOnChange(false);
    setIsDifference(false);
    if (!!singleHistoryMessage) {
      setValue('messageId', singleHistoryMessage.messageId);
      setValue('title', singleHistoryMessage.title);
      setValue('contents', singleHistoryMessage.contents);
      setValue('date', dayjs(singleHistoryMessage.sendDate).format('YYYY-MM-DD'));
      setValue('hour', dayjs(singleHistoryMessage.sendDate).format('HH'));
      setValue('minute', dayjs(singleHistoryMessage.sendDate).format('mm'));
      setValue('recipent_infos', singleHistoryMessage.recipent_infos);
      setValue('sendDate', singleHistoryMessage.sendDate);
      if (singleHistoryMessage.sendMode) {
        setValue('sendMode', singleHistoryMessage.sendMode);
      }
    }
  };
  return (
    <>
      <MessageLayout
        titleComponent={
          <TitleDiv>
            <IconBoxFlat width={20} height={20} />
            <Text
              styleName="headline1"
              color="RG02"
              sx={{
                fontSize: 18,
                fontFamily: 'Pretendard',
                fontWeight: 700,
                width: 'calc(100% - 300px)',
              }}
            >
              {!!singleHistoryMessage ? singleHistoryMessage?.title : '발송 이력'}
            </Text>
            {!!singleHistoryMessage && !isOnChange && (
              <AddRecipientButton
                type="button"
                variant="secondary"
                h={32}
                onClick={() => {
                  // TODO: 수신인 으로 이동 처리 + 수신인 선택 처리
                  return onClickAddRecipientButton();
                }}
              >
                <IconPlugBlue />
                <Text
                  styleName="headline3"
                  color="RG02"
                  sx={{
                    color: theme.colors.RC02,
                    fontFamily: 'Pretendard',
                    fontSize: 13,
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 20 /* 153.846% */,
                    letterSpacing: -0.26,
                    width: 200,
                  }}
                >
                  이 수신인에게 새 메시지 작성하기
                </Text>
              </AddRecipientButton>
            )}
          </TitleDiv>
        }
        messageComponent={
          singleHistoryMessage ? (
            requestParams?.tabType === 'MANUAL_MESSAGE' ? (
              <ChangeMessageComponent isOnChange={isOnChange} watch={watch} setValue={setValue} />
            ) : requestParams?.tabType === 'AUTO_MESSAGE' ? (
              <FixedMessageComponent contents={singleHistoryMessage?.contents ?? ''} />
            ) : (
              <FixedMessageComponent
                contents={singleHistoryMessage?.contents ?? ''}
                exceptionEndTime={singleHistoryMessage?.exceptionEndTime ?? null}
                exceptionStartTime={singleHistoryMessage?.exceptionStartTime ?? null}
              />
            )
          ) : (
            <CoverDiv>
              <div>발송 이력을 선택하면</div>
              <div>여기서 내역을 조회할 수 있습니다.</div>
            </CoverDiv>
          )
        }
        buttonComponent={
          !!requestParams &&
          requestParams?.tabType === 'MANUAL_MESSAGE' &&
          singleHistoryMessage?.sendMode &&
          dayjs(singleHistoryMessage?.sendDate).isAfter(dayjs().add(10, 'minute')) && (
            <MainMessageButtonDiv
              isOnChange={isOnChange}
              watch={watch}
              onClickCancel={onClickCancel}
              onClickCancelReservation={onClickCancelReservation}
              onClickModify={onClickModify}
              onClickModifyComplete={onClickModifyComplete}
            />
          )
        }
      />
      <AlertModal
        type="WARNING"
        isOpen={isOpenCancelReservationModal}
        messages={
          <Stack sx={{ gap: '8px' }}>
            <Text styleName="body2" color="RG02">
              정말 예약 발송을 취소하시겠습니까?
            </Text>
            <Text styleName="body2" color="RG02">
              발송 취소된 메시지는 복구할 수 없습니다.
            </Text>
          </Stack>
        }
        buttonGroup={
          <ModalButtonDiv>
            <Button
              variant="tertiary"
              h={40}
              sx={{ width: '100%', marginTop: '40px' }}
              onClick={() => {
                setIsOpenCancelReservationModal(false);
              }}
            >
              <Text styleName="body1" color="RG03">
                닫기
              </Text>
            </Button>
            <Button
              variant="primary"
              h={40}
              sx={{ width: '100%', marginTop: '40px' }}
              onClick={onClickRequestCancelReservation}
            >
              <Text styleName="body1" color="RG00">
                예약 발송 취소
              </Text>
            </Button>
          </ModalButtonDiv>
        }
      />
      <AlertModal
        type="WARNING"
        isOpen={isOpenCancelChangeModal}
        messages={
          <Stack sx={{ gap: '8px' }}>
            <Text styleName="body2" color="RG02">
              수정을 취소하시겠습니까?
            </Text>
            <Text styleName="body2" color="RG02">
              작성 중이던 내용은 저장되지 않습니다.
            </Text>
          </Stack>
        }
        buttonGroup={
          <ModalButtonDiv>
            <Button
              variant="tertiary"
              h={40}
              sx={{ width: '100%', marginTop: '40px' }}
              onClick={() => setIsOpenCancelChangeModal(false)}
            >
              <Text styleName="body1" color="RG02">
                계속 작성
              </Text>
            </Button>
            <Button
              variant="primary"
              h={40}
              sx={{ width: '100%', marginTop: '40px' }}
              onClick={onClickModalWriteCancel}
            >
              <Text styleName="body1" color="RG00">
                작성 취소
              </Text>
            </Button>
          </ModalButtonDiv>
        }
      />
      <RequestErrorModal
        isOpen={isOpenCancelReservationFailModal}
        setIsOpen={setIsOpenCancelReservationFailModal}
        errorMessage="예약 발송 취소 요청에 실패하였습니다."
      />
      <RequestErrorModal
        isOpen={isOpenModifyFailModal}
        setIsOpen={setIsOpenModifyFailModal}
        errorMessage="메시지 수정 요청에 실패하였습니다."
      />
    </>
  );
};

export default MainMessageComponent;

const TitleDiv = styled.div<StackProps>`
  display: flex;
  padding: 20px;
  align-items: center;
  gap: 10px;
  align-self: stretch;

  ${({ theme }) => ({
    borderBottom: `1px solid ${theme.colors.RG06}`,
  })}
`;
const AddRecipientButton = styled(Button)`
  display: flex;
  height: 32px;
  padding: 2px 10px 2px 8px;
  justify-content: center;
  align-items: center;
  gap: 6px;

  border-radius: 4px;
  border: 1px solid var(--Primary-Blue700, #2e66f6);

  background: var(--legacy-RG00, #fff);
`;
const CoverDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1 0 0;
  align-self: stretch;

  color: var(--GrayScale-gray700, #7d818d);
  text-align: center;
  font-feature-settings: 'liga' off, 'clig' off;

  /* body14M */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;
const ModalButtonDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  align-self: stretch;
`;
